import React, {memo, useState, useMemo, useCallback, useEffect} from 'react';
import Breadcrumbs from "@/app/components/breadcrumbs";
import {BreadcrumbsType} from "@/app/components/breadcrumbs/widget";
import styles from "./styles.module.scss";
import MapWithMarkers from "@/app/components/map_with_points/widget";
import CircularProgressIndicator from "@/app/components/circular_progress_indicator";
import {normalizeCoords, useMediaQuery} from "@/app/utils/custom_function";
import PlacesWidget from "@/app/features/map/components/places_widget/widget";
import CreateCourtWidget from "@/app/components/map_with_points/components/select_points_sheet_widget";
import CourtInfoWidget from "@/app/components/court_info_widget/widget";
import FiltersForMap from "@/app/components/filters_for_map";
import BottomSheet from "@/app/components/bottom_sheet/widget";
import useMap from "./slice";
import useMapWithPoints from "@/app/components/map_with_points/slice";
import Button from "@/app/components/button";
import LazyAssets from '@/app/utils/assets_widget';
import { logEventSafe } from '@/app/utils/analytics';

const MapPage = () => {
  const {state, uploadSelectedCourt, applyFilters, setCoords, loadMoreCourts} = useMap();
  const {
    state: mapState,
    setType,
    setBooking,
    setArtificialLight,
    setLockerRooms,
    setParkingAvailability,
    setMinRating,
    clearFilters,
    toggleFilters,
    calculateFilterCount,
    toggleCreateCourt,
    setCreatedMarker,
    goToCreateMatch,
    setCoachAvailable,
    setSocialVibe,
    setHasCafeOrBar,
    setRentalGearAvailable,
  } = useMapWithPoints();
  const isDesktop = useMediaQuery('(min-width:1024px)');

  // Search state management
  const [searchTerm, setSearchTerm] = useState("");

  // Filter courts based on search term
  const filteredCourts = useMemo(() => {
    if (!searchTerm.trim() || !state.courts) {
      return state.courts;
    }
    return state.courts.filter(court => 
      court.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [state.courts, searchTerm]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  // Auto-load more pages when search results are insufficient
  useEffect(() => {
    const loadMoreIfNeeded = async () => {
      // Only auto-load if:
      // 1. There's a search term
      // 2. Filtered results are fewer than desired page size
      // 3. There are more pages available
      // 4. Not already loading
      if (
        searchTerm.trim() &&
        filteredCourts &&
        filteredCourts.length < state.pageSize &&
        state.hasMorePages &&
        !state.isLoadingMore
      ) {
        try {
          await loadMoreCourts(mapState.filters);
        } catch (error) {
          console.error('Error loading more courts:', error);
        }
      }
    };

    loadMoreIfNeeded();
  }, [searchTerm, filteredCourts, state.hasMorePages, state.isLoadingMore, state.pageSize, loadMoreCourts, mapState.filters]);

  // Log court search when user types a query (debounced by 500ms using effect timing)
  useEffect(() => {
    if (!searchTerm.trim()) return;
    const id = setTimeout(() => {
      const loc = state.user ? `${state.user.lastLat},${state.user.lastLng}` : undefined;
      const filtersUsed = Object.keys(mapState.filters || {}).filter((k) => (mapState.filters as any)[k] !== undefined).join(',') || undefined;
      logEventSafe('padel_court_search', {
        search_location: loc,
        filters_used: filtersUsed,
      });
    }, 500);
    return () => clearTimeout(id);
  }, [searchTerm, mapState.filters, state.user]);

  let sidePanel = <PlacesWidget courts={filteredCourts} selectCourt={uploadSelectedCourt}/>;
  if (mapState.showCreateCourt) {
    sidePanel = <CreateCourtWidget coords={mapState.pointSelected} onClose={toggleCreateCourt} setCoords={setCreatedMarker}/>;
  } else if (state.court) {
    sidePanel = <div className={styles.courtInfo}>
      <CourtInfoWidget data={state.court} reviews={state.reviews} goToCreateMatch={goToCreateMatch}/>
    </div>;
  }

  return (
    <div className={styles.map}>
      <Breadcrumbs items={[BreadcrumbsType.padel, BreadcrumbsType.map]}/>
      {state.courts && state.user && isDesktop && (
        <div className={styles.sidePanel}>
          {state.court &&
              <Button icon={<LazyAssets.Back className={styles.icon}/>} onClick={() => uploadSelectedCourt()} circle
                      className={styles.back}/>}
          {mapState.showFilters && <FiltersForMap
              filters={mapState.filters}
              setArtificialLight={setArtificialLight}
              setType={(type) => setType(type, applyFilters)}
              setLockerRooms={setLockerRooms}
              setParkingAvailability={setParkingAvailability}
              setBookingRequired={setBooking}
              setCoachAvailable={setBooking}
              setSocialVibe={setBooking}
              setHasCafeOrBar={setBooking}
              setRentalGearAvailable={setBooking}
              setMinRating={setMinRating}
              clearFilters={clearFilters}
              applyFilters={(filters) => {
                applyFilters(filters);
                toggleFilters();
              }}
          />}
          {sidePanel}</div>
      )}
      {state.courts && state.user && (
        <MapWithMarkers
          center={mapState.showCreateCourt && mapState.pointSelected ?
            normalizeCoords(mapState.pointSelected) :
            { lat: state.user?.lastLat ?? 0, lng: state.user?.lastLng ?? 0 }
          }
          markers={filteredCourts || []}
          court={state.court}
          onSelectCourt={uploadSelectedCourt}
          courtLoading={state.courtLoading}
          reviews={state.reviews}
          applyFilters={applyFilters}
          loadMoreCourts={loadMoreCourts}
          state={mapState}
          paginationState={state}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          setType={setType}
          setBooking={setBooking}
          setArtificialLight={setArtificialLight}
          setLockerRooms={setLockerRooms}
          setParkingAvailability={setParkingAvailability}
          setMinRating={setMinRating}
          clearFilters={clearFilters}
          toggleFilters={toggleFilters}
          calculateFilterCount={calculateFilterCount}
          toggleCreateCourt={toggleCreateCourt}
          setCreatedMarker={setCreatedMarker}
          isDesktop={isDesktop}
        />
      )}
      {state.courts === undefined && state.user === undefined ? <CircularProgressIndicator isGreen/> : null}

      {!isDesktop && mapState.showCreateCourt && (
        <BottomSheet open={mapState.showCreateCourt} onClose={toggleCreateCourt}>
          <CreateCourtWidget coords={mapState.pointSelected} onClose={toggleCreateCourt} setCoords={setCreatedMarker}/>
        </BottomSheet>
      )}
      {!isDesktop && mapState.showFilters && (
        <BottomSheet open={mapState.showFilters} onClose={toggleFilters}>
          <FiltersForMap
            filters={mapState.filters}
            setArtificialLight={setArtificialLight}
            setType={(type) => setType(type, applyFilters)}
            setLockerRooms={setLockerRooms}
            setParkingAvailability={setParkingAvailability}
            setBookingRequired={setBooking}
            setMinRating={setMinRating}
            setCoachAvailable={setCoachAvailable}
            setSocialVibe={setSocialVibe}
            setHasCafeOrBar={setHasCafeOrBar}
            setRentalGearAvailable={setRentalGearAvailable}
            clearFilters={clearFilters}
            applyFilters={(filters) => {
              applyFilters(filters);
              toggleFilters();
            }}

          />
        </BottomSheet>
      )}
      {!isDesktop && state.court && (
        <BottomSheet open={!!state.court} onClose={() => uploadSelectedCourt()}>
          <CourtInfoWidget data={state.court} reviews={state.reviews} goToCreateMatch={goToCreateMatch}/>
        </BottomSheet>
      )}
    </div>
  );
};

export default memo(MapPage);